import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import { useAgent } from "agents/react";
import { useAgentChat } from "@cloudflare/think/react";
import type { UIMessage } from "ai";
import {
  AGENT,
  AGENT_INSTANCE,
  CORE,
  apiRequest,
  linkifyText,
  messageText,
  type AgentChannel,
  type PendingAction,
} from "./lib/api";

type AgentClientState = {
  pendingActions?: PendingAction[];
  channel?: AgentChannel;
  sessionCookie?: string | null;
};

const SUGGESTIONS = [
  "Show my products and prices",
  "Create a 5% discount on Saffron Date Collection until tomorrow",
  "Show available tenant payable",
];

function BubbleText({ text }: { text: string }) {
  return (
    <>
      {linkifyText(text).map((part, index) =>
        typeof part === "string" ? (
          <span key={index}>{part}</span>
        ) : (
          <a
            key={index}
            href={part.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {part.url}
          </a>
        ),
      )}
    </>
  );
}

type ShellProps = {
  channel: AgentChannel;
  setChannel: (channel: AgentChannel) => void;
  draft: string;
  setDraft: (value: string) => void;
  busy: boolean;
  connectionError: Error | null;
  visibleMessages: Array<{ id: string; role: "user" | "assistant"; text: string }>;
  openApprovals: PendingAction[];
  onSend: (text: string) => Promise<void>;
  onDecide: (pendingId: string, confirm: boolean) => Promise<void>;
  onReset: () => Promise<void>;
  onLogout: () => Promise<void>;
  messagesRef: RefObject<HTMLDivElement | null>;
};

function OperatorShell(props: ShellProps) {
  return (
    <main className="app">
      <aside className="sidebar">
        <div className="brand">
          <span className="mark">OC</span>OnCall Next
        </div>
        <div className="workspace">
          <div className="label">Workspace</div>
          <strong>Green Basket Demo</strong>
          <span>Owner access · isolated staging</span>
        </div>
        <div className="channel-title">Talk to the same operator through</div>
        <div className="channels">
          <button
            className={`channel ${props.channel === "chat" ? "active" : ""}`}
            type="button"
            onClick={() => props.setChannel("chat")}
          >
            <span className="channel-icon">◈</span>
            <span>OnCall chat</span>
          </button>
          <button
            className={`channel ${props.channel === "whatsapp" ? "active" : ""}`}
            type="button"
            onClick={() => props.setChannel("whatsapp")}
          >
            <span className="channel-icon">◉</span>
            <span>WhatsApp test</span>
          </button>
        </div>
        <div className="side-copy">
          Chat uses Cloudflare Think for streaming + durable session memory.
          WhatsApp simulation still hits the same Core Code Mode agent over REST.
        </div>
        <div className="side-links">
          <a href="https://store.next.oncallos.com">Open generated store</a>
          <a href="https://next.oncallos.com">Open system preview</a>
        </div>
      </aside>

      <section className="chat">
        <header className="chat-head">
          <div className="chat-person">
            <div className="avatar">AI</div>
            <div>
              <strong>
                {props.channel === "whatsapp"
                  ? "WhatsApp operator test"
                  : "OnCall operator"}
                <span className="harness-chip">Think</span>
              </strong>
              <div className="presence">
                <span
                  className={`presence-dot ${
                    props.channel === "chat" && props.connectionError
                      ? "offline"
                      : ""
                  }`}
                />
                <span>
                  {props.channel === "whatsapp"
                    ? "Same durable agent · simulated transport"
                    : props.connectionError
                      ? "Reconnecting stream…"
                      : "Live Core · streaming memory"}
                </span>
              </div>
            </div>
          </div>
          <div className="head-actions">
            <button type="button" onClick={() => void props.onReset()}>
              ↻ <span>New conversation</span>
            </button>
            <button type="button" onClick={() => void props.onLogout()}>
              ⇥ <span>Sign out</span>
            </button>
          </div>
        </header>

        <div className="messages" ref={props.messagesRef}>
          {props.visibleMessages.length === 0 ? (
            <div className="welcome">
              <div className="avatar">AI</div>
              <h1>What should we do?</h1>
              <p>
                I can inspect your live business data and make changes through
                the new OnCall Core. Replies stream live, and durable session
                memory keeps context across turns.
              </p>
              <div className="suggestions">
                {SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion}
                    className="suggestion"
                    type="button"
                    onClick={() => void props.onSend(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {props.visibleMessages.map((message) => (
            <div key={message.id} className={`message ${message.role}`}>
              <div className="bubble">
                <BubbleText text={message.text} />
                <div className="meta">
                  {props.channel === "whatsapp" ? "WhatsApp" : "OnCall chat"} ·
                  Think
                </div>
              </div>
            </div>
          ))}

          {props.openApprovals.map((action) => (
            <article key={action.id} className="approval">
              <div className="approval-head">
                <span>◇</span>Approval needed
              </div>
              <p>{action.reason || "Confirm this exact business change."}</p>
              <code>{action.capabilityId}</code>
              <div className="approval-actions">
                <button
                  className="primary"
                  type="button"
                  onClick={() => void props.onDecide(action.id, true)}
                >
                  Confirm exact change
                </button>
                <button
                  className="danger"
                  type="button"
                  onClick={() => void props.onDecide(action.id, false)}
                >
                  Cancel
                </button>
              </div>
            </article>
          ))}

          {props.busy ? (
            <div className="typing">
              The operator is working through the live Core…
            </div>
          ) : null}
        </div>

        <footer className="composer-wrap">
          <div className="composer">
            <textarea
              rows={1}
              value={props.draft}
              placeholder="Ask about products, orders, customers, discounts, or payments…"
              onChange={(event) => props.setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  void props.onSend(props.draft);
                }
              }}
            />
            <button
              className="send"
              type="button"
              aria-label="Send"
              disabled={props.busy || !props.draft.trim()}
              onClick={() => void props.onSend(props.draft)}
            >
              ↑
            </button>
          </div>
          <div className="composer-note">
            {props.channel === "whatsapp"
              ? "This sends through the WhatsApp adapter path without contacting Meta."
              : "Streaming Think session · high-risk changes pause for exact approval."}
          </div>
        </footer>
      </section>
    </main>
  );
}

function AuthenticatedApp({
  onLogout,
}: {
  onLogout: () => Promise<void>;
}) {
  const [channel, setChannel] = useState<AgentChannel>("chat");
  const [draft, setDraft] = useState("");
  const [restBusy, setRestBusy] = useState(false);
  const [restMessages, setRestMessages] = useState<
    Array<{ id: string; role: "user" | "assistant"; text: string }>
  >([]);
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
  const messagesRef = useRef<HTMLDivElement>(null);

  const agent = useAgent<AgentClientState>({
    agent: "NextGoldenAgent",
    name: AGENT_INSTANCE,
    onStateUpdate: (state) => {
      if (Array.isArray(state?.pendingActions)) {
        setPendingActions(state.pendingActions);
      }
    },
  });

  const {
    messages,
    sendMessage,
    status,
    clearHistory,
    isStreaming,
    connectionError,
  } = useAgentChat<AgentClientState, UIMessage>({
    agent,
  });

  const streaming =
    status === "submitted" || status === "streaming" || isStreaming;
  const busy = streaming || restBusy;

  const visibleMessages = useMemo(() => {
    if (channel === "whatsapp") return restMessages;
    return messages
      .filter(
        (message) => message.role === "user" || message.role === "assistant",
      )
      .map((message) => ({
        id: message.id,
        role: message.role as "user" | "assistant",
        text: messageText(message),
      }))
      .filter((message) => message.text.length > 0);
  }, [channel, messages, restMessages]);

  const openApprovals = useMemo(
    () => pendingActions.filter((action) => action.status === "pending"),
    [pendingActions],
  );

  const scrollToBottom = useCallback(() => {
    const node = messagesRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [visibleMessages, openApprovals, streaming, scrollToBottom]);

  const refreshRestHistory = useCallback(async () => {
    const body = await apiRequest<{
      state?: {
        transcript?: Array<{
          id: string;
          role: "user" | "assistant";
          text: string;
        }>;
        pendingActions?: PendingAction[];
      };
    }>(AGENT, "/api/v1/agent/history");
    const transcript = body.state?.transcript ?? [];
    setRestMessages(
      transcript.map((entry) => ({
        id: entry.id,
        role: entry.role,
        text: entry.text,
      })),
    );
    if (Array.isArray(body.state?.pendingActions)) {
      setPendingActions(body.state.pendingActions);
    }
  }, []);

  useEffect(() => {
    void refreshRestHistory().catch(() => undefined);
  }, [refreshRestHistory]);

  async function send(text: string) {
    const message = text.trim();
    if (!message || busy) return;
    setDraft("");

    if (channel === "whatsapp") {
      setRestBusy(true);
      try {
        await apiRequest(AGENT, "/api/v1/whatsapp/simulate", {
          method: "POST",
          body: JSON.stringify({
            requestId: crypto.randomUUID(),
            message,
            channel: "whatsapp",
          }),
        });
        await refreshRestHistory();
      } catch (error) {
        alert(error instanceof Error ? error.message : String(error));
      } finally {
        setRestBusy(false);
      }
      return;
    }

    await sendMessage({ text: message });
  }

  async function decide(pendingId: string, confirm: boolean) {
    try {
      await apiRequest(
        AGENT,
        confirm ? "/api/v1/agent/confirm" : "/api/v1/agent/cancel",
        { method: "POST", body: JSON.stringify({ pendingId }) },
      );
      await refreshRestHistory();
    } catch (error) {
      alert(error instanceof Error ? error.message : String(error));
    }
  }

  async function resetConversation() {
    if (busy) return;
    await apiRequest(AGENT, "/api/v1/agent/reset", {
      method: "POST",
      body: "{}",
    });
    clearHistory();
    setRestMessages([]);
    setPendingActions([]);
  }

  return (
    <OperatorShell
      channel={channel}
      setChannel={setChannel}
      draft={draft}
      setDraft={setDraft}
      busy={busy}
      connectionError={connectionError}
      visibleMessages={visibleMessages}
      openApprovals={openApprovals}
      onSend={send}
      onDecide={decide}
      onReset={resetConversation}
      onLogout={onLogout}
      messagesRef={messagesRef}
    />
  );
}

export function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [accessCode, setAccessCode] = useState("");
  const [loginError, setLoginError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const session = await apiRequest<{ authenticated?: boolean }>(
          CORE,
          "/api/v1/session",
        );
        if (cancelled) return;
        if (session.authenticated) setAuthenticated(true);
      } catch {
        // stay on login
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function login() {
    setLoginError("");
    try {
      await apiRequest(CORE, "/api/v1/session/login", {
        method: "POST",
        body: JSON.stringify({ code: accessCode }),
      });
      setAuthenticated(true);
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : String(error));
    }
  }

  async function logout() {
    await apiRequest(CORE, "/api/v1/session/logout", {
      method: "POST",
      body: "{}",
    });
    location.reload();
  }

  if (bootstrapping) {
    return (
      <div className="login">
        <div className="login-card">Loading…</div>
      </div>
    );
  }

  return (
    <>
      {authenticated ? <AuthenticatedApp onLogout={logout} /> : null}
      <div className={`login ${authenticated ? "hidden" : ""}`}>
        <div className="login-card">
          <div className="mark">OC</div>
          <h2>Open OnCall Next</h2>
          <p>
            This is the new isolated operator experience. Enter the staging
            access code to continue.
          </p>
          <input
            type="password"
            autoComplete="one-time-code"
            placeholder="Staging access code"
            value={accessCode}
            onChange={(event) => setAccessCode(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void login();
            }}
          />
          <button className="primary" type="button" onClick={() => void login()}>
            Continue
          </button>
          <div className="error">{loginError}</div>
        </div>
      </div>
    </>
  );
}
